const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

class SSEService {
  constructor() {
    this.eventSource = null;
    this.estabelecimentoId = null;
    this.listeners = new Map(); // Map<evento, Set<callback>>
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnecting = false;
  }

  // Conectar ao SSE de um estabelecimento
  connect(estabelecimentoId) {
    if (!estabelecimentoId) {
      console.warn('⚠️ [SSE] ID do estabelecimento não fornecido');
      return;
    }

    // Se já está conectado ao mesmo estabelecimento, não reconectar
    if (this.eventSource && this.estabelecimentoId === estabelecimentoId && this.eventSource.readyState === EventSource.OPEN) {
      console.log('✅ [SSE] Já conectado ao estabelecimento', estabelecimentoId);
      return;
    }

    // Fechar conexão anterior se existir
    this.disconnect();

    this.estabelecimentoId = estabelecimentoId;
    this.isConnecting = true;

    console.log(`🔌 [SSE] Conectando ao estabelecimento ${estabelecimentoId}...`);

    this.eventSource = new EventSource(`${API_URL}/sse/${estabelecimentoId}`);

    // Evento de conexão estabelecida
    this.eventSource.addEventListener('connected', (event) => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      console.log('✅ [SSE] Conectado com sucesso!', JSON.parse(event.data));
    });

    // Evento de novo pedido
    this.eventSource.addEventListener('novo_pedido', (event) => {
      const { dados } = JSON.parse(event.data);
      console.log('🔔 [SSE] Novo pedido recebido:', dados);
      this._emitToListeners('novo_pedido', dados);
    });

    // Evento de pedido atualizado
    this.eventSource.addEventListener('pedido_atualizado', (event) => {
      const { dados } = JSON.parse(event.data);
      console.log('🔔 [SSE] Pedido atualizado:', dados);
      this._emitToListeners('pedido_atualizado', dados);
    });

    // Nova mensagem de WhatsApp (entrada ou saída) — inbox de atendimento humano
    this.eventSource.addEventListener('whatsapp_mensagem', (event) => {
      const { dados } = JSON.parse(event.data);
      this._emitToListeners('whatsapp_mensagem', dados);
    });

    // Cliente pediu pra falar com atendente pelo bot do WhatsApp
    this.eventSource.addEventListener('whatsapp_atendimento_solicitado', (event) => {
      const { dados } = JSON.parse(event.data);
      console.log('🙋 [SSE] Atendimento humano solicitado:', dados);
      this._emitToListeners('whatsapp_atendimento_solicitado', dados);
    });

    // Evento genérico de mensagem (fallback)
    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 [SSE] Mensagem recebida:', data);
        if (data.evento) {
          this._emitToListeners(data.evento, data.dados);
        }
      } catch (e) {
        console.log('📨 [SSE] Mensagem raw:', event.data);
      }
    };

    // Evento de erro
    this.eventSource.onerror = (error) => {
      console.error('❌ [SSE] Erro na conexão:', error);
      this.isConnecting = false;

      if (this.eventSource.readyState === EventSource.CLOSED) {
        console.log('🔌 [SSE] Conexão fechada');
        this._tentarReconectar();
      }
    };
  }

  // Tentar reconectar automaticamente
  _tentarReconectar() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ [SSE] Máximo de tentativas de reconexão atingido');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.log(`🔄 [SSE] Tentando reconectar em ${delay}ms... (tentativa ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.estabelecimentoId) {
        this.connect(this.estabelecimentoId);
      }
    }, delay);
  }

  // Desconectar
  disconnect() {
    if (this.eventSource) {
      console.log('🔌 [SSE] Desconectando...');
      this.eventSource.close();
      this.eventSource = null;
    }
    this.estabelecimentoId = null;
    this.isConnecting = false;
  }

  // Registrar listener para um evento
  on(evento, callback) {
    if (!this.listeners.has(evento)) {
      this.listeners.set(evento, new Set());
    }
    this.listeners.get(evento).add(callback);
    console.log(`📝 [SSE] Listener registrado para '${evento}'`);
  }

  // Remover listener de um evento
  off(evento, callback) {
    const eventListeners = this.listeners.get(evento);
    if (eventListeners) {
      if (callback) {
        eventListeners.delete(callback);
        console.log(`📝 [SSE] Listener removido para '${evento}'`);
      } else {
        // Remover todos os listeners do evento
        this.listeners.delete(evento);
        console.log(`📝 [SSE] Todos os listeners removidos para '${evento}'`);
      }
    }
  }

  // Emitir evento para todos os listeners registrados
  _emitToListeners(evento, dados) {
    const eventListeners = this.listeners.get(evento);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(dados);
        } catch (e) {
          console.error(`❌ [SSE] Erro ao executar listener de '${evento}':`, e);
        }
      });
    }
  }

  // Verificar se está conectado
  isConnected() {
    return this.eventSource && this.eventSource.readyState === EventSource.OPEN;
  }

  // Obter estado da conexão
  getState() {
    if (!this.eventSource) return 'CLOSED';
    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING: return 'CONNECTING';
      case EventSource.OPEN: return 'OPEN';
      case EventSource.CLOSED: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  }
}

// Exportar instância única (singleton)
export const sseService = new SSEService();

export default sseService;
